<template>
  <div class="pagination">
    <button
      class="pagination-btn"
      type="button"
      aria-label="First page"
      :disabled="currentPage === 1"
      @click="$emit('update:currentPage', 1)"
    >
      <Icon name="page-first" />
    </button>
    <button
      class="pagination-btn"
      type="button"
      aria-label="Previous page"
      :disabled="currentPage === 1"
      @click="$emit('update:currentPage', Math.max(1, currentPage - 1))"
    >
      <Icon name="page-prev" />
    </button>
    <div class="pagination-numbers">
      <button
        v-if="showEllipsisStart"
        :key="1"
        class="pagination-number"
        type="button"
        @click="$emit('update:currentPage', 1)"
      >
        1
      </button>
      <span v-if="showEllipsisStart" class="pagination-ellipsis">...</span>
      <button
        v-for="page in visiblePages"
        :key="page"
        :class="['pagination-number', { active: currentPage === page }]"
        type="button"
        @click="$emit('update:currentPage', page)"
      >
        {{ page }}
      </button>
      <span v-if="showEllipsisEnd" class="pagination-ellipsis">...</span>
      <button
        v-if="showEllipsisEnd"
        :key="totalPages"
        class="pagination-number"
        type="button"
        @click="$emit('update:currentPage', totalPages)"
      >
        {{ totalPages }}
      </button>
    </div>
    <button
      class="pagination-btn"
      type="button"
      aria-label="Next page"
      :disabled="currentPage === totalPages"
      @click="$emit('update:currentPage', Math.min(totalPages, currentPage + 1))"
    >
      <Icon name="page-next" />
    </button>
    <button
      class="pagination-btn"
      type="button"
      aria-label="Last page"
      :disabled="currentPage === totalPages"
      @click="$emit('update:currentPage', totalPages)"
    >
      <Icon name="page-last" />
    </button>
    <div class="pagination-select">
      <span class="pagination-label">Page</span>
      <div class="pagination-dropdown" ref="dropdownRef">
        <button class="pagination-dropdown-trigger" type="button" @click="toggleDropdown">
          <span class="pagination-dropdown-value">{{ currentPage }}</span>
          <Icon name="page-down" class="pagination-dropdown-icon" />
        </button>
        <div v-if="isDropdownOpen" class="pagination-dropdown-menu">
          <button
            v-for="page in totalPages"
            :key="page"
            :class="['pagination-dropdown-option', { active: currentPage === page }]"
            type="button"
            @click="selectPage(page)"
          >
            {{ page }}
          </button>
        </div>
      </div>
      <span class="pagination-label">of {{ totalPages }}</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'Pagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
  },
  emits: ['update:currentPage'],
  setup(props, { emit }) {
    const isDropdownOpen = ref(false);
    const dropdownRef = ref(null);

    const visiblePages = computed(() => {
      const total = props.totalPages;
      const current = props.currentPage;
      const pages = [];

      // 页数较少时，全部展示，无需省略号
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      // 靠近开头：展示前 5 页（含 1），避免当前页处于窗口边缘而看不到后续页码
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5);
      } else if (current >= total - 3) {
        // 靠近末尾：展示后 5 页（含末页）
        pages.push(total - 4, total - 3, total - 2, total - 1, total);
      } else {
        // 中间：以当前页为中心展示 5 页，确保前后页码均可见
        pages.push(current - 2, current - 1, current, current + 1, current + 2);
      }
      return pages;
    });

    const showEllipsisStart = computed(() => {
      if (props.totalPages <= 7) return false;
      // 可见窗口未包含第 1 页时，单独展示 1 + 省略号
      return visiblePages.value[0] > 1;
    });

    const showEllipsisEnd = computed(() => {
      if (props.totalPages <= 7) return false;
      const last = visiblePages.value[visiblePages.value.length - 1];
      // 可见窗口未包含末页时，单独展示省略号 + 末页
      return last < props.totalPages;
    });

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const selectPage = (page) => {
      emit('update:currentPage', page);
      isDropdownOpen.value = false;
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        isDropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      visiblePages,
      showEllipsisStart,
      showEllipsisEnd,
      isDropdownOpen,
      dropdownRef,
      toggleDropdown,
      selectPage,
    };
  },
};
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
  flex-wrap: wrap;

  &-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 24px;
    color: $black;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      border-color: $blue-night;
      color: $blue-night;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &-numbers {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &-number {
    width: 32px;
    height: 32px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: $white;
    color: $grey-black;
    font-size: 14px;
    cursor: pointer;
    font-weight: 700;
    transition: all 0.2s ease;

    &.active {
      background: $blue-night;
      border-color: $blue-night;
      color: $white;
    }

    &:hover:not(.active) {
      border-color: $blue-night;
      color: $blue-night;
    }
  }

  &-ellipsis {
    color: #999;
    padding: 0 4px;
    font-size: 14px;
  }

  &-select {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 16px;
  }

  &-dropdown {
    position: relative;
    width: 60px;

    &-trigger {
      width: 100%;
      height: 32px;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 0 8px;
      font-size: 13px;
      cursor: pointer;
      background: $white;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        border-color: $blue-night;
      }

      &:focus {
        outline: none;
        border-color: $blue-night;
      }
    }

    &-value {
      color: $grey-black;
      font-weight: 700;
    }

    &-icon {
      width: 14px;
      height: 14px;
      color: $grey-black;
      transition: transform 0.2s ease;
    }

    &-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: $white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      z-index: 100;
      max-height: 200px;
      overflow-y: auto;
    }

    &-option {
      width: 100%;
      padding: 8px;
      font-size: 14px;
      font-weight: 700;
      text-align: left;
      background: transparent;
      border: none;
      cursor: pointer;
      color: $grey-black;
      transition: all 0.2s ease;

      &:hover {
        background-color: $blue-night;
        color: $white;
      }

      &.active {
        background-color: $blue-night;
        color: $white;
      }
    }
  }

  &-label {
    font-size: 13px;
    color: $grey-black;
  }
}

@include tablet-landscape {
  .pagination {
    gap: 8px;
    margin-top: 40px;

    &-btn {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 24px;
    }

    &-numbers {
      gap: 6px;
    }

    &-number {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 14px;
    }

    &-dropdown {
      width: 70px;

      &-trigger {
        height: 36px;
        font-size: 14px;
      }
    }

    &-label {
      font-size: 14px;
    }
  }
}
</style>
